<template>
  <div>
    <Select
      v-model="user.isPartner"
      :class="selectClass"
      :data-test-id="`${dataTestIdPrefix}__AreYouAPartner`"
      :label="inputFields.partner.label"
      large
      :name="inputFields.partner.name"
      :options="getPartnerTypeOptions"
      rules="required"
      @blur="sendInputEnteredEvent(inputFields.partner)"
    />
    <TransitionWrapper name="slideVertical">
      <Select
        v-if="user.isPartner === 'Yes'"
        v-model="user.partnerType"
        :class="selectClass"
        :data-test-id="`${dataTestIdPrefix}__PartnerTypeSelect`"
        :label="inputFields.partnerType.label"
        large
        :name="inputFields.partnerType.name"
        :options="getPartnerOptions"
        rules="required"
        @blur="sendInputEnteredEvent(inputFields.partnerType)"
        @input="handlePartnerTypeChange"
      />
    </TransitionWrapper>
    <SeverityModal
      :data-test-id="`${dataTestIdPrefix}__SeverityModal`"
      :notificationMessage="notificationMessage"
      :showCloseButton="false"
      :showModal="showMspConfirmModal"
      title="Hi, MSP"
      variant="sev2"
    >
      <template v-slot:subtitle>
        <span>Are you sure you want an MSP account?</span>
      </template>
      <template v-slot:body>
        <ParagraphBody slot:body>
          You're about to create a Multi-Tenant Portal (MTP) account, which allows you to create
          and manage multiple organizations from the MTP Portal.
        </ParagraphBody>
        <ParagraphBody>
          Would you like to continue?
        </ParagraphBody>
      </template>
      <template v-slot:learnmore>
        <ParagraphBody>
          For more information, visit our
          <ButtonNoBackground
            :data-test-id="`${dataTestIdPrefix}__KbLink`"
            :href="kbLink"
            isExternal
            target="_blank"
            :text="kbLinkText"
            type="informational"
            @click.native="handleKbLinkClick"
          />
        </ParagraphBody>
      </template>
      <template v-slot:footer>
        <div :class="$style.btnContainer">
          <ButtonSecondary
            :data-test-id="`${dataTestIdPrefix}__CancelMspButton`"
            :text="cancelMspText"
            @click.native="handleCancelMsp($event)"
          />
          <Button
            :class="$style.confirm"
            :data-test-id="`${dataTestIdPrefix}__ConfirmMspButton`"
            :text="confirmMspText"
            @click.native="handleConfirmMsp($event)"
          />
        </div>
      </template>
    </SeverityModal>
  </div>
</template>

<script>
import {
  Button,
  ButtonNoBackground,
  ButtonSecondary,
  ParagraphBody,
  Select,
  SeverityModal,
  TransitionWrapper,
} from '@jumpcloud/ui-components';
import { eventLocations, eventProperties } from 'client/GrowthEngineering/util/GrowthConstants';
import { mapActions, mapGetters, mapState } from 'vuex';
import EventHelper from 'client/GrowthEngineering/EventHelper';

const {
  description,
  displayLogic,
  location,
  section,
  text,
  toUrl,
} = eventProperties;

export default {
  name: 'PartnerPicker',

  components: {
    Button,
    ButtonNoBackground,
    ButtonSecondary,
    ParagraphBody,
    Select,
    SeverityModal,
    TransitionWrapper,
  },

  EventHelper,

  props: {
    dataTestIdPrefix: {
      type: String,
      default: '',
    },
    selectClass: {
      type: String,
      default: '',
    },
  },

  data: () => ({
    cancelMspText: 'No, I Need A Standard Account',
    confirmMspText: 'Yes, I\'m Sure',
    kbLink: 'https://support.jumpcloud.com/support/s/article/JumpCloud-Multi-Tenant-Portal-User-Interface-Early-Access',
    kbLinkText: 'Knowledge Base',
    notificationMessage: 'Be advised, this account type is different from a standard admin account.',
    showMspConfirmModal: false,
    inputFields: {
      partner: {
        label: 'I am a',
        name: 'partner',
      },
      partnerType: {
        label: 'My primary business function is',
        name: 'partner type',
      },
    },
  }),

  computed: {
    ...mapGetters([
      'getPartnerOptions',
      'getPartnerTypeOptions',
    ]),

    ...mapState(['step', 'user']),
  },

  methods: {
    ...mapActions([
      'sendInputEnteredEvent',
    ]),

    handleCancelMsp($event) {
      $event.preventDefault();
      this.user.isPartner = 'No';
      this.user.partnerType = null;
      this.showMspConfirmModal = false;

      this.$options.EventHelper.triggerButtonClicked({
        [description]: 'Cancels MSP partner type selection, changes account selection to standard account',
        [location]: eventLocations.signup,
        [section]: `Signup Step ${this.step} - MSP pop-up`,
        [text]: this.cancelMspText,
      });
    },

    handleConfirmMsp($event) {
      $event.preventDefault();
      this.showMspConfirmModal = false;

      this.$options.EventHelper.triggerButtonClicked({
        [description]: 'Confirms MSP partner type selection, closes modal',
        [location]: eventLocations.signup,
        [section]: `Signup Step ${this.step} - MSP pop-up`,
        [text]: this.confirmMspText,
      });
    },

    handleKbLinkClick() {
      this.$options.EventHelper.triggerLinkClicked({
        [description]: 'Opens KB Article in a separate tab',
        [location]: eventLocations.signup,
        [section]: `Signup Step ${this.step} - MSP pop-up`,
        [text]: this.kbLinkText,
        [toUrl]: this.kbLink,
      });
    },

    handlePartnerTypeChange(partnerType) {
      if (partnerType === 'MSP') {
        this.showMspConfirmModal = true;
        this.$options.EventHelper.triggerModalViewed({
          [description]: 'MSP Pop-up modal opened',
          [displayLogic]: 'Dispayed when a user selects "MSP" for partner type on signup',
          [location]: eventLocations.signup,
          [section]: `Signup Step ${this.step}`,
        });
      }
    },
  },
};
</script>

<style module scoped>
@value (max-width-small) from '~css/breakpoints.css';

.confirm {
  margin-left: var(--jc-spacer);
}

@media (max-width: max-width-small) {
  .btnContainer {
    display: flex;
    flex-direction: column;
  }

  .confirm {
    margin-left: 0;
    margin-top: var(--jc-spacer);
  }
}
</style>
